import { SelectOption } from "../../../molecule/inputs/select-input"
import { CSSProperties, useEffect, useMemo, useState } from "react"
import useEnterSearch from "hooks/useEnterSearch"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import { IProjectQueryParam } from "models/project"
import SearchSelect from "Components/molecule/inputs/search-select"
import MultiSelect from "Components/molecule/inputs/multi-select"
import SearchFormContainer from "Components/atoms/containers/search-form-container"
import { orderBy } from "lodash"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import TextInput from "Components/molecule/inputs/text-input"

interface SearchProjectsProps {
    handleChangeQuery: (key: string, value: any) => any,
    query: IProjectQueryParam
    projectManagers: SelectOption[]
    customers: SelectOption[]
    branches: SelectOption[]
    status: SelectOption[]
    department: SelectOption[]
    // Project Health filter; only rendered when options are passed (project list)
    health?: SelectOption[]
    handleClearSearch: () => any
    handleSearch: () => any
    style?: CSSProperties
    hide?: {
        code?: boolean,
        customers?: boolean,
        status?: boolean,
        department?: boolean
    },
    fields?: {
        department?: string,
        projectManager?: string
    }
    isSearching: boolean
    // Fit all filters in one row on desktop
    singleRow?: boolean
}

const SearchProjectsForm = ({
    query,
    handleChangeQuery,
    projectManagers,
    branches,
    customers,
    status,
    department,
    health,
    handleClearSearch,
    handleSearch,
    hide,
    fields,
    isSearching,
    singleRow
}: SearchProjectsProps) => {

    const { inputProps, selectProps } = useEnterSearch(handleSearch)
    const [codeInput, setCodeInput] = useState(query.code ?? '')

    const departmentKey = fields?.department ?? 'departmentIds'
    const projectManagerKey = fields?.projectManager ?? 'projectManagerIds'

    const customerValue = useMemo(() => {
        const ids: string[] = (query.clientIds as string[]) ?? []
        return customers.filter(o => ids.includes(o.value))
    }, [query.clientIds, customers])

    const branchValue = useMemo(() => {
        const ids: string[] = (query.branchIds as string[]) ?? []
        return branches.filter(o => ids.includes(o.value))
    }, [query.branchIds, branches])

    const departmentValue = useMemo(() => {
        const ids: string[] = (query[departmentKey] as string[]) ?? []
        return department.filter(o => ids.includes(o.value))
    }, [query, departmentKey, department])

    const projectManagerValue = useMemo(() => {
        const ids: string[] = (query[projectManagerKey] as string[]) ?? []
        return projectManagers.filter(o => ids.includes(o.value))
    }, [query, projectManagerKey, projectManagers])

    const statusValue = useMemo(() => {
        const ids: string[] = (query.statusIds as string[]) ?? []
        return status.filter(o => ids.includes(o.value))
    }, [query.statusIds, status])

    const healthValue = useMemo(() => {
        const values: string[] = (query.healthStatuses as string[]) ?? []
        return (health ?? []).filter(o => values.includes(o.value))
    }, [query.healthStatuses, health])

    useEffect(() => {
        setCodeInput(query.code ?? '')
    }, [query.code])

    useEffect(() => {
        const timeout = setTimeout(() => {
            handleChangeQuery('code', codeInput)
        }, 300)

        return () => clearTimeout(timeout)
    }, [codeInput])

    return <SearchFormContainer
        compact={singleRow}
        inputs={<>
            <MultiSelect
                className="col"
                label={"Branch"}
                value={branchValue}
                onChange={(selected) => handleChangeQuery('branchIds', selected.map(s => s.value))}
                options={branches}
                {...selectProps}
            />
            {
                !hide?.department &&
                <MultiSelect
                    className="col"
                    label={"Department"}
                    value={departmentValue}
                    onChange={(selected) => handleChangeQuery(departmentKey, selected.map(s => s.value))}
                    options={department}
                    {...selectProps}
                />
            }
            {
                !hide?.code &&
                <TextInput
                    className="col"
                    value={codeInput}
                    label={"Project Code"}
                    onChange={(e) => setCodeInput(e?.target.value ?? '')}
                    {...inputProps}
                    placeholder="Project Code"
                />
            }
            {
                !hide?.customers &&
                <MultiSelect
                    className="col"
                    label={"Customer"}
                    value={customerValue}
                    onChange={(selected) => handleChangeQuery('clientIds', selected.map(s => s.value))}
                    options={customers}
                    {...selectProps}
                />
            }
            <MultiSelect
                className="col"
                label={"Project Manager"}
                value={projectManagerValue}
                onChange={(selected) => handleChangeQuery(projectManagerKey, selected.map(s => s.value))}
                options={projectManagers}
                {...selectProps}
            />

            {
                !hide?.status &&
                <MultiSelect
                    className="col"
                    label={"Status"}
                    value={statusValue}
                    onChange={(selected) => handleChangeQuery('statusIds', selected.map(s => s.value))}
                    options={status}
                    {...selectProps}
                />
            }
            {
                health &&
                <MultiSelect
                    className="col"
                    label={"Health"}
                    value={healthValue}
                    onChange={(selected) => handleChangeQuery('healthStatuses', selected.map(s => s.value))}
                    options={health}
                    {...selectProps}
                />
            }
        </>}
        clearSearchButton={
            <>
                <PrimaryButton
                    style={{
                        minWidth: '150px'
                    }}
                    disabled={isSearching}
                    onClick={handleSearch}
                >
                    {isSearching ? 'Searching...' : 'Search'}
                </PrimaryButton>
                <SecondaryButton
                    style={{
                        minWidth: '100px'
                    }}
                    disabled={isSearching}
                    onClick={handleClearSearch}
                >
                    Clear Search
                </SecondaryButton>
            </>
        }
    />

}

export default SearchProjectsForm